import { components } from "@api/api";
import { FilterInput } from "@vms/SelectFilters/SelectFiltersTypes";
import { FilterWord } from "@pages/TopTrends/TopTrendsContext";
import { Sort } from "@models";
import { getTopTrendsApiSortingDirection, TopTrendsSortingColumnsEnum } from "@models/TopTrends";
import { ByoAnalyticType, ByoValueType, ByoWord, SlicesEnum } from "@models/Byo";

export type AiSettingsResponse = components["schemas"]["AiSettingsOutput"];

export type TopTrendsAiSettings = {
    apiFilters: FilterInput;
    topTrendsCategoriesIds: number[];
    includeLessOnePercentPenetration: boolean;
    includeLessTenMenusCount: boolean;
    showOnlyNewItems: boolean;
    drillWords: FilterWord[];
    growthPeriod: number | null;
    sortingColumn: TopTrendsSortingColumnsEnum;
    sortingDirection: Sort;
}

export type ByoAiSettings = {
    apiFilters: FilterInput;
    words: NonNullable<ByoWord['keyword']>[];
    yearsGrowth: number | null;
    activeSliceId: SlicesEnum | null;
    semanticFilter: string | null;
    activeValueType: ByoValueType | null;
    activeAnalyticsType: ByoAnalyticType | null;
    sortBy: string | null;
}

/**
 * Project the API's TopTrends filter onto the shape the page's filter state and
 * the AI-settings chips read. Returns null when the query didn't resolve to a
 * TopTrends page.
 */
export function toTopTrendsAiSettings(response: AiSettingsResponse | null): TopTrendsAiSettings | null {
    if (!response?.topTrendFilter) return null;

    const {
        filters,
        topTrendCategories,
        includeLessOnePercentPenetration,
        includeLessTenMenusCount,
        isNew,
        drillWords,
        yearsGrowth,
    } = response.topTrendFilter.mainFilter ?? {};

    const {
        sortColumn,
        sortDir,
    } = response.topTrendFilter.tableParameters ?? {};

    return {
        apiFilters: filters ?? {},
        topTrendsCategoriesIds: (topTrendCategories ?? []).map(i => i.id ?? 0),
        includeLessOnePercentPenetration: includeLessOnePercentPenetration ?? false,
        includeLessTenMenusCount: includeLessTenMenusCount ?? false,
        showOnlyNewItems: isNew ?? false,
        drillWords: drillWords ?? [],
        growthPeriod: yearsGrowth ?? null,
        sortingColumn: sortColumn ?? TopTrendsSortingColumnsEnum.Penetration,
        sortingDirection: getTopTrendsApiSortingDirection(sortDir),
    };
}

/** As above for BYO, which carries its display preferences separately. */
export function toByoAiSettings(response: AiSettingsResponse | null): ByoAiSettings | null {
    if (!response?.byoFilter || !response?.byoUiSettings) return null;

    const {
        filters: byoFilters,
        words,
    } = response.byoFilter;

    const {
        yearsGrowth,
        selectedSlice,
        semanticFilter,
        showAbsoluteValues,
        selectedAnalyticsType,
        sortBy,
    } = response.byoUiSettings;

    return {
        apiFilters: byoFilters ?? {},
        words: words ?? [],
        yearsGrowth: yearsGrowth ?? null,
        activeSliceId: selectedSlice ?? null,
        semanticFilter: semanticFilter ?? null,
        activeValueType: showAbsoluteValues ? ByoValueType.Count : null,
        activeAnalyticsType: selectedAnalyticsType ?? null,
        sortBy: !!sortBy ? sortBy : null,
    };
}
